import { useEffect } from 'react'

import useIsPartner from '~/features/partners/hooks/useIsPartner'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import { getCorrespondentDomain } from '~/features/partners/utils'
import EEnvironmentVariables from '~/models/config'
import { store } from '~/store'

type TWindow = Window & {
  agentId?: string
  baseurl?: string
  CHATBOT_SVG?: string
  initChatbotEmbed?: () => void
  customImage?: string
  isSupportChat?: boolean
}

const chatbotEmbedIconForPartners =
  '<svg viewBox="-9 -9 41 41" style="background-color: rgb(0, 0, 0); width: 55px; height: 55px; border-radius: 8px; box-shadow: rgba(0, 0, 0, 0.25) 0px 4px 4px; fill: rgb(255, 255, 255);"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2M9 11H7V9h2zm4 0h-2V9h2zm4 0h-2V9h2z"></path></svg>'

const chatbotEmbedIconForHeyLibby =
  '<svg viewBox="-9 -9 41 41" style="background-color: rgb(57, 66, 168); width: 55px; height: 55px; border-radius: 50%; box-shadow: rgba(0, 0, 0, 0.25) 0px 4px 4px; fill: rgb(255, 255, 255);"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2M9 11H7V9h2zm4 0h-2V9h2zm4 0h-2V9h2z"></path></svg>'

function ChatbotEmbed() {
  const userId = store((state) => state.user.id)
  const { partner, isLoading } = usePartnerCentralized()
  const isPartner = useIsPartner()

  useEffect(() => {
    if (isLoading) return

    const getNonPartnerAgentId = () => {
      const supportAgentId = import.meta.env[
        EEnvironmentVariables.VITE_CHATBOT_AGENT_ID
      ]
      const marketingAgentId =
        import.meta.env[
          EEnvironmentVariables.VITE_CHATBOT_MARKETING_AGENT_ID
        ] || supportAgentId

      return userId ? supportAgentId : marketingAgentId
    }

    const setEmbeddedChatbot = () => {
      ;(window as TWindow).agentId =
        isPartner && partner.chatAgentId
          ? partner.chatAgentId
          : getNonPartnerAgentId()
      ;(window as TWindow).baseurl =
        `https://${partner.domain || getCorrespondentDomain()}/`
      ;(window as TWindow).CHATBOT_SVG =
        isPartner && partner.showEmbeddedChat
          ? chatbotEmbedIconForPartners
          : chatbotEmbedIconForHeyLibby
      ;(window as TWindow).isSupportChat = !isPartner

      const baseScriptUrl = `${(window as TWindow).baseurl}js/chatbot-embed.js`
      const isAlreadyAppended = document.head.querySelector(
        `script[src^="${baseScriptUrl}"]`,
      )

      if (!isAlreadyAppended) {
        const chatbotEmbedScript = document.createElement('script')
        chatbotEmbedScript.src = `${baseScriptUrl}?${Date.now()}`
        chatbotEmbedScript.onload = () => {
          ;(window as TWindow).initChatbotEmbed?.()
        }
        document.head.appendChild(chatbotEmbedScript)
      } else {
        ;(window as TWindow).initChatbotEmbed?.()
      }
    }

    if (document.readyState === 'complete') {
      setEmbeddedChatbot()
    } else {
      window.addEventListener('load', setEmbeddedChatbot)
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('load', setEmbeddedChatbot)
    }
  }, [
    isPartner,
    isLoading,
    partner.chatAgentId,
    partner.domain,
    partner.showEmbeddedChat,
    userId,
  ])
  return null
}

export default ChatbotEmbed
